import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useQuery } from "@tanstack/react-query";
import {
  Module,
  PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT,
} from "@/constants";
import { ProjectService } from "@/modules/projects/service/project";
import PageHeader from "@/components/PageHeader";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import PageContent from "@/components/PageContent";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/Spinner";
import { BasicDataForm } from "@/modules/projects/components/invoiceSpecifications/section/BasicDataForm";
import { invoiceSpecificationsService } from "@/services/invoiceSpecifications";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useToast } from "@/components/ui/use-toast";
import { useSettlementId } from "../../hooks/useSettlementId";
import { useNavigate } from "react-router-dom";
import { generatePath } from "react-router-dom";
import { useInvoiceSpecificationsFormDefaultValues } from "./hooks/useInvoiceSpecificationsFormDefaultValues";
import { ClientService } from "@/modules/client/service";
import { ProjectSettlementService } from "@/modules/projectSettlements/service/projectSettlements";
import { WriteInvoiceSpecificationForm } from "@/schemas/invoiceSpecificationForm";
import { useEffect, useState } from "react";
import { useInvoiceSpecificationsCreatorBreadcrumbs } from "./hooks/useInvoiceSpecificationsCreatorBreadcrumbs";
import { ReadInvoiceSpecification } from "@/types/invoiceSpecification";
import { useInitialStores } from "./hooks/useInitialStores";

export default function ProjectInvoiceSpecificationsCreator() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const routeProjectId = useProjectId();
  const settlementId = useSettlementId();

  const { t } = useTranslation();

  const [projectId, setProjectId] = useState<string | undefined>(
    routeProjectId,
  );

  const generateEditPath = (invoiceSpecificationId: string) =>
    routeProjectId
      ? generatePath(PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT, {
          projectId: routeProjectId,
          settlementId,
          invoiceSpecificationId,
        })
      : generatePath(
          PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT,
          {
            settlementId,
            invoiceSpecificationId,
          },
        );

  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId, "invoice-specifications"] as const,
    queryFn: () => ProjectService.get(projectId),
    enabled: Boolean(projectId),
  });

  const projectDependsOn = projectQuery.data?.project_depends_on;

  const clientQuery = useQuery({
    queryKey: [Module.Clients, projectQuery.data?.client_id] as const,
    queryFn: () => ClientService.get(projectQuery.data?.client_id),
    enabled: Boolean(projectQuery.data),
  });

  const settlementQuery = useQuery({
    queryKey: [Module.ProjectSettlements, settlementId] as const,
    queryFn: () => ProjectSettlementService.get(settlementId),
  });

  useEffect(() => {
    if (!routeProjectId && settlementQuery.data?.project_id) {
      setProjectId(settlementQuery.data.project_id);
    }
  }, [settlementQuery.data, routeProjectId]);

  const breadcrumbs = useInvoiceSpecificationsCreatorBreadcrumbs({
    projectId,
    settlementId,
  });

  const initialStores = useInitialStores({
    projectId,
    settlementId,
    projectDependsOn,
  });

  const defaultValues = useInvoiceSpecificationsFormDefaultValues({
    period: settlementQuery.data?.period,
    project: projectQuery.data,
    client: clientQuery.data,
    specificationInvoicingCompany:
      settlementQuery.data?.specification_invoicing_company,
  });

  const mutateInvoiceSpecificationData = async (
    values: WriteInvoiceSpecificationForm,
  ): Promise<ReadInvoiceSpecification> =>
    invoiceSpecificationsService.postSpecification(values);

  const invoiceSpecificationMutation = useMutationWithErrorHandling({
    mutationFn: (data: WriteInvoiceSpecificationForm) =>
      mutateInvoiceSpecificationData(data),
    onSuccess: (data: ReadInvoiceSpecification) => {
      toast({ title: t("projectInvoiceSpecifications.success.create") });
      navigate(generateEditPath(data.id));
    },
    errorTitle: t("projectInvoiceSpecifications.error.create"),
  });

  if (
    projectQuery.isPending ||
    clientQuery.isPending ||
    settlementQuery.isPending ||
    initialStores.isPending
  ) {
    return (
      <Spinner
        label={t("projectInvoiceSpecifications.loading")}
        className="w-full h-full"
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />
      <PageContent title={t("projectInvoiceSpecifications.title.create")}>
        <div className="grid grid-cols-1 grid-rows-[auto_1fr] gap-4">
          <ProjectDetailsAndNavigation projectId={projectId} />
          <BasicDataForm<WriteInvoiceSpecificationForm>
            onSubmit={invoiceSpecificationMutation.mutateAsync}
            values={defaultValues}
            projectName={projectQuery.data?.name}
            isLoading={invoiceSpecificationMutation.isPending}
            transformData={(formData) => ({
              ...formData,
              code: defaultValues.code || "",
              stores: initialStores.stores,
              client_id: clientQuery.data?.id || "",
              period_id: settlementQuery.data?.id || "",
              primary_manager_id:
                settlementQuery.data?.primary_manager_id || "",
              services_and_goods_id:
                projectQuery.data?.services_and_goods_id || "",
              invoicing_company_id:
                projectQuery.data?.invoicing_company_id || "",
            })}
          />
        </div>
      </PageContent>
    </>
  );
}
